import React from 'react';
import type { NavigateFunction } from 'react-router-dom';
import { Tooltip } from '@hydrodigital/design-system';
import HydrodigitalLogo from '@/assets/img/hydrodigital-logo.svg?url';
import HydrodigitalSimpleLogo from '@/assets/img/hydrodigital-simple-logo.svg?url';
import ComboboxLocations from '@/components/General/ComboboxLocations';
import type { DashboardContextValueInterface } from '@/contexts/DashboardContext';
import type { UserLocation } from '@/domain/Location/Location';
import type { AppDispatch } from '@/redux/store';
import { restartResumeAndReadingList } from '@/redux/slices/readingsResumesSlice';
import { getMapsAction } from '@/redux/slices/mapSlice';
import { publishDashboardMapEvent } from '@/events/dashboardMapEvents';
import NwlLogo from '../NwlLogo';
import TooltipLabel from './TooltipLabel';

interface HeaderBrandProps {
	companyName: string;
	isXLScreen: boolean;
	isSmallScreen: boolean;
	width: number;
	pathname: string;
	selectedLocation: string;
	locations: UserLocation[] | null;
	navigate: NavigateFunction;
	dispatch: AppDispatch;
	setContextValue: React.Dispatch<React.SetStateAction<DashboardContextValueInterface>>;
	handleClickSelect: (value: number | string) => void;
}

const HeaderBrand: React.FC<HeaderBrandProps> = ({
	companyName,
	isXLScreen,
	isSmallScreen,
	width,
	pathname,
	selectedLocation,
	locations,
	navigate,
	dispatch,
	setContextValue,
	handleClickSelect,
}) => {
	const handleLocationSelect = (value: number | string) => {
		publishDashboardMapEvent({ type: 'resetDma' });
		publishDashboardMapEvent({ type: 'closePopup' });
		handleClickSelect(value);
	};

	const handleLogoAction = async () => {
		const match = /^\/diagram-block\/(\d+)/.exec(pathname ?? '');
		const routeLocationId = match?.[1] ? Number(match[1]) : null;
		const defaultLoc =
			locations?.find((l) => l?.defaultSelected)?.locationId ??
			locations?.[0]?.locationId ??
			null;
		const targetLocation = routeLocationId || selectedLocation || defaultLoc;
		dispatch(restartResumeAndReadingList());
		setContextValue((prev) => ({ ...prev, selectedSensors: [] }));
		if (targetLocation != null) {
			handleClickSelect(Number(targetLocation));
			await dispatch(getMapsAction(String(targetLocation)));
		}
		navigate('/');
	};

	return (
		<div
			className="flex items-center leading-normal"
			style={{
				backgroundColor: 'white',
				margin: '10px',
				borderRadius: '8px',
				padding: '10px 10px',
				paddingRight: '10px',
				paddingLeft: '10px',
				marginRight: '10px',
			}}
		>
			<button
				type="button"
				aria-label="Go to dashboard"
				onClick={handleLogoAction}
				style={{
					background: 'transparent',
					border: 'none',
					padding: 0,
					cursor: 'pointer',
				}}
			>
				{isXLScreen ? (
					<img src={HydrodigitalLogo} alt="logo" style={{ width: 220 }} />
				) : (
					<img src={HydrodigitalSimpleLogo} alt="logo" style={{ height: 24 }} />
				)}
			</button>

			{width >= 1280 && (
				<>
					<div
						style={{
							width: '1px',
							height: '23px',
							backgroundColor: '#E6E6E6',
						}}
						className="ml-1 mr-1"
					/>
					<span
						style={{
							fontSize: '16px',
							fontWeight: 'bold',
							color: '#434B56',
							textAlign: 'center',
							lineHeight: '20px',
							display: 'flex',
						}}
					>
						{companyName === 'Northumbrian Water' ? (
							<NwlLogo
								onClick={() => {
									navigate('/nwl');
								}}
							/>
						) : (
							companyName
						)}
					</span>
				</>
			)}

			<Tooltip position="right" minWidth={20} slot={<TooltipLabel>Locations</TooltipLabel>}>
				<div style={{ display: 'flex', alignItems: 'center', flexDirection: 'row' }}>
					<div
						style={{
							width: '1px',
							height: '23px',
							backgroundColor: '#E6E6E6',
						}}
						className="ml-1 mr-1"
					/>
					<ComboboxLocations
						aditionalStyle={{
							minWidth: isSmallScreen ? 140 : 180,
							width: isSmallScreen ? 140 : 180,
							paddingTop: 5,
						}}
						handleClickSelect={handleLocationSelect}
						size="large"
					/>
				</div>
			</Tooltip>
		</div>
	);
};

export default HeaderBrand;
